import React, { useState } from "react";

import { selectVersionsByProjectId } from "fond/api";
import { useCanViewBomComparison } from "fond/hooks/useCanViewBom";
import { useGetScenarioConfiguration } from "fond/hooks/useGetScenarioConfiguration";
import { useVersionBoms } from "fond/hooks/useVersionBoms";
import { BomComparisonTable } from "fond/project/bom/BomComparisonTable";
import { BomModalProps } from "fond/project/bom/index";
import { setVersion } from "fond/project/redux";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, Message, Modal } from "fond/widgets";

const BomComparisonModal: React.FC<BomModalProps> = ({ title = "Materials and Costs", project, onClose }: BomModalProps) => {
  const dispatch = useAppDispatch();
  const currentVersionId = useAppSelector((state) => state.project.versionId);
  const { configuration: scenarioConfiguration } = useGetScenarioConfiguration({ projectId: project.ID });
  const versions = useAppSelector((state) => selectVersionsByProjectId(state, project.ID))
    ?.slice()
    .reverse();
  const versionIds = versions?.map((version) => version.ID) ?? [];
  const { boms, isLoading } = useVersionBoms(versionIds);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const baselineVersion = versions?.[0];
  const hasBaselineBom = !baselineVersion || !!boms[baselineVersion.ID];
  const canViewBomComparison = useCanViewBomComparison(project);

  const handleOpenScenario = (versionId: string) => {
    dispatch(setVersion(versionId));
    onClose();
  };

  if (!canViewBomComparison) {
    return (
      <Modal
        open
        onClose={onClose}
        variant="primary"
        header={title}
        showClose
        data-testid="bom-comparison-modal"
        content={<Message type="error">This feature is not available.</Message>}
      />
    );
  }

  if (!isLoading && !hasBaselineBom) {
    return (
      <Modal
        open
        onClose={onClose}
        variant="primary"
        header={title}
        showClose
        data-testid="bom-comparison-modal"
        content={<Message type="warning">The baseline's BOM is not available.</Message>}
      />
    );
  }

  return (
    <Modal
      showFullscreen
      showClose
      open
      onClose={onClose}
      onFullscreenChange={setIsFullscreen}
      variant="primary"
      header={title}
      headerSubtitle="COLLABORATE"
      data-testid="bom-comparison-modal"
      PaperProps={{ style: { width: "max(60vw, 1100px)", maxWidth: "max(60vw, 1100px)" } }}
      DialogContentProps={{ style: { display: "flex", flexDirection: "column", minHeight: 0 } }}
      content={
        isLoading ? (
          <BlockSpinner />
        ) : (
          <BomComparisonTable
            versions={versions ?? []}
            boms={boms}
            currency={scenarioConfiguration?.Currency}
            systemOfMeasurement={project.SystemOfMeasurement}
            currentVersionId={currentVersionId}
            onOpenScenario={handleOpenScenario}
            isFullscreen={isFullscreen}
          />
        )
      }
    />
  );
};

export default BomComparisonModal;
